// `React` must be in SCOPE, not merely resolvable: the kiosk renderchecks run ts-node with
// `"jsx": "react"` (the classic runtime), which compiles this file's JSX to
// `React.createElement`. The app bundlers use the automatic runtime and need no import, so a
// missing one here builds clean and throws only in those node-only gates.
import React from "react";
import Svg, { Path } from "react-native-svg";

/**
 * A `›` — the forward mark on a CTA, a disclosure row or a step's Next button.
 *
 * ## What this replaced, and why the export was a file of raw SVG
 *
 * The file held the designer's export verbatim — an `<svg>` element at the top level of a
 * `.tsx`, which is not a component and does not compile — with `stroke="white"` baked in. White
 * is right on a primary CTA and invisible on the white sheet a disclosure row sits on, so the
 * colour has to be the CALLER's: `color` is required for exactly that reason, and there is no
 * default that would be right in both places.
 *
 * The export's own `<clipPath>` went with it. It clipped the drawing to a 20×20 rect through a
 * `matrix(0 1 1 0 0 0)` transform — a diagonal flip that maps the box onto itself — so it
 * removed nothing from a stroke already inside the box. Carrying it would mean carrying a
 * document-wide `id` into every instance of this icon on the page, and duplicate ids in one
 * document are the classic way an SVG icon renders blank in one place and not another.
 *
 * `strokeLinecap`/`strokeLinejoin` are `round`, which the export omitted: at 2.4 stroke units a
 * butt cap ends the chevron in two hard corners that read as a broken arrow at kiosk sizes.
 */
export function ChevronRight({
  color,
  size = 20,
}: {
  /** Required — see above. On a primary CTA this is the button's foreground. */
  color: string;
  /** Both axes; the source is square. */
  size?: number;
}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <Path
        d="M7.07107 3.22581L14.1421 10.2969L7.07107 17.3679"
        stroke={color}
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}
ChevronRight.displayName = "ChevronRight";
export default ChevronRight;
